import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import SEO from "@/components/SEO";
import { 
  Briefcase, 
  Users, 
  Monitor, 
  Coffee, 
  Wifi, 
  Utensils,
  Quote,
  ArrowRight,
  MapPin,
  Phone
} from "lucide-react";

const images = {
  hero: "https://customer-assets.emergentagent.com/job_work-space-hub/artifacts/5xi2y6ua_Conference%20Room%20%28Ocean%29.jpg",
  lobby: "https://customer-assets.emergentagent.com/job_work-space-hub/artifacts/a06ekh31_Lobby.jpg",
  mainRoom: "https://customer-assets.emergentagent.com/job_work-space-hub/artifacts/drhm18b5_Main%20Room%203.jpg",
  conference: "https://customer-assets.emergentagent.com/job_work-space-hub/artifacts/5xi2y6ua_Conference%20Room%20%28Ocean%29.jpg",
  office1: "https://customer-assets.emergentagent.com/job_work-space-hub/artifacts/d6ok7fck_Interior%20Office%201.jpg",
  office2: "https://customer-assets.emergentagent.com/job_work-space-hub/artifacts/meesobcs_Interior%20Office%204.jpg",
  entrance: "https://customer-assets.emergentagent.com/job_work-space-hub/artifacts/0yum9doa_Lobby%202.jpg",
};

const services = [
  {
    icon: Briefcase,
    title: "Private Offices",
    description: "Fully-furnished private office rentals in Satellite Beach & Indialantic. Ocean views and professional environment for your business.",
    link: "/offices",
    image: images.office1,
  },
  {
    icon: Users,
    title: "Coworking",
    description: "Flexible coworking in Brevard County. Hot desks, dedicated desks, and memberships for remote workers and entrepreneurs.",
    link: "/coworking",
    image: images.mainRoom,
  },
  {
    icon: Monitor,
    title: "Conference Room",
    description: "Oceanview conference room in Satellite Beach with 65\" TV. Perfect for client meetings and presentations in Brevard County.",
    link: "/conference",
    image: images.conference,
  },
];

const amenities = [
  { icon: Wifi, title: "Super-Fast WiFi", description: "Enterprise-grade internet for seamless work" },
  { icon: Coffee, title: "Gourmet Coffee", description: "Complimentary coffee bar & kitchen access" },
  { icon: Utensils, title: "Restaurant On-Site", description: "Dining downstairs and across the street" },
  { icon: Monitor, title: "65\" TV Display", description: "Modern tech in our conference room" },
  { icon: Users, title: "Community Events", description: "Monthly networking and learning sessions" },
  { icon: MapPin, title: "Beachside Location", description: "Steps from the ocean on A1A" },
];

const testimonials = [
  {
    name: "Katie P.",
    role: "Freelancer/Writer",
    text: "I've been searching for a quiet place to get out of the house and get some work done – but didn't want to drive over the bridge every time. Local coffee shops were too loud, and WiFi could be sluggish. My flexible pass at Coastal Collaborative allows me to use a desk or conference room if I need it. So glad you're beachside!",
  },
  {
    name: "Mike S.",
    role: "Business Owner",
    text: "We were quickly running out of space at our growing business, and I needed to find somewhere to have our meetings. The 65\" TV & oceanfront view from their conference room really impressed our clients and vendors.",
  },
];

const fadeUp = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.6 }
};

export const HomePage = () => {
  return (
    <div className="pt-20" data-testid="home-page">
      <SEO
        title="Coworking Space Brevard County FL | Office Rentals Satellite Beach & Indialantic | Coastal Collaborative"
        description="Beachside's only coworking space. 3,000 sqft of oceanview offices, coworking desks, and a conference room in Satellite Beach, FL. Serving Brevard County, Indialantic, Melbourne Beach & Indian Harbour Beach."
        canonical="https://www.workcoastal.com/"
      />
      {/* Hero Section */}
      <section className="relative h-[90vh] min-h-[600px] flex items-center" data-testid="hero-section">
        <div className="absolute inset-0">
          <img
            src={images.hero}
            alt="Ocean view conference room"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 hero-overlay" />
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
          <motion.div 
            className="max-w-2xl"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <span className="inline-block font-sans text-sm font-medium text-sea-light uppercase tracking-wider mb-4">
              Coworking Brevard County Florida
            </span>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight mb-6">
              Beachside's Only
              <span className="block text-sand-light">Coworking Space</span>
            </h1>
            <p className="font-sans text-lg text-white/90 leading-relaxed mb-8 max-w-xl">
              Premier office rentals in Satellite Beach & Indialantic. 3,000 sqft of oceanview offices, 
              coworking desks, conference room, kitchen + coffee bar & super-fast WiFi. 
              The top coworking space in Brevard County, Florida.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link to="/contact">
                <Button 
                  size="lg" 
                  className="bg-sea hover:bg-sea-light text-white rounded-full px-8 btn-primary"
                  data-testid="hero-cta-button"
                >
                  Request a Tour
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
              <a href="tel:321-866-8572">
                <Button 
                  size="lg" 
                  variant="outline" 
                  className="bg-white/10 backdrop-blur border-white/30 text-white hover:bg-white/20 rounded-full px-8"
                  data-testid="hero-phone-button"
                >
                  <Phone className="mr-2 h-5 w-5" />
                  (321) 866-8572
                </Button>
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Services Section - Bento Grid */}
      <section className="py-24 bg-white" data-testid="services-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            className="text-center max-w-2xl mx-auto mb-16"
            {...fadeUp}
          >
            <h2 className="font-serif text-3xl sm:text-4xl font-bold text-brand mb-4">
              Office Rentals & Coworking in Satellite Beach
            </h2>
            <p className="font-sans text-muted-foreground leading-relaxed">
              Whether you need a private office in Indialantic, a flexible coworking desk in Satellite Beach, 
              or a professional meeting space in Brevard County, we have the perfect solution.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <motion.div
                key={service.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <Link 
                  to={service.link}
                  className="block group"
                  data-testid={`service-card-${service.title.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  <div className="bg-white rounded-2xl overflow-hidden border border-sand/30 card-hover">
                    <div className="aspect-[4/3] overflow-hidden">
                      <img
                        src={service.image}
                        alt={service.title}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                      />
                    </div>
                    <div className="p-8">
                      <div className="w-12 h-12 rounded-full bg-sand-light flex items-center justify-center mb-4">
                        <service.icon className="w-6 h-6 text-brand" strokeWidth={1.5} />
                      </div>
                      <h3 className="font-serif text-xl font-semibold text-brand mb-2">
                        {service.title}
                      </h3>
                      <p className="font-sans text-sm text-muted-foreground leading-relaxed">
                        {service.description}
                      </p>
                      <span className="inline-flex items-center font-sans text-sm font-medium text-sea mt-4 group-hover:gap-2 transition-all">
                        Learn More <ArrowRight className="ml-1 w-4 h-4" />
                      </span>
                    </div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* About / Gallery Section */}
      <section className="py-24 bg-sand-light/30" data-testid="about-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <span className="inline-block font-sans text-sm font-medium text-sea uppercase tracking-wider mb-4">
                About Us
              </span>
              <h2 className="font-serif text-3xl sm:text-4xl font-bold text-brand mb-6 leading-tight">
                Brevard County's Premier Coworking Community
              </h2>
              <p className="font-sans text-muted-foreground leading-relaxed mb-6">
                We carefully designed & renovated this 3,000 square foot ocean-view space to be a 
                coastal/modern, community focused workspace. Serving Satellite Beach, Indialantic, 
                Melbourne Beach, and all of Brevard County with 16 dedicated offices, 
                an ocean-view conference room & flexible coworking desks.
              </p>
              <p className="font-sans text-muted-foreground leading-relaxed mb-8">
                Coastal Collaborative is Beachside Brevard County, Florida's first & only coworking space. 
                We're more than just office rentals – we're a community of freelancers, entrepreneurs, and 
                remote workers who believe work should be inspiring.
              </p>
              <Link to="/contact">
                <Button 
                  className="bg-brand text-white hover:bg-brand-light rounded-full px-8 btn-primary"
                  data-testid="about-cta-button"
                >
                  Schedule Your Visit
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
            </motion.div>

            <motion.div
              className="grid grid-cols-2 gap-4"
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <div className="space-y-4">
                <img
                  src={images.lobby}
                  alt="Coastal Collaborative Lobby"
                  className="rounded-2xl image-hover w-full aspect-[3/4] object-cover"
                />
              </div>
              <div className="space-y-4 pt-8">
                <img
                  src={images.entrance}
                  alt="Coastal Collaborative Entrance"
                  className="rounded-2xl image-hover w-full aspect-[3/4] object-cover"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Amenities Section */}
      <section className="py-24 bg-white" data-testid="amenities-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            className="text-center max-w-2xl mx-auto mb-16"
            {...fadeUp}
          >
            <h2 className="font-serif text-3xl sm:text-4xl font-bold text-brand mb-4">
              Everything You Need to Work
            </h2>
            <p className="font-sans text-muted-foreground leading-relaxed">
              Modern amenities included with your membership. Focus on your work while we handle the rest.
            </p>
          </motion.div>

          <div className="grid grid-cols-2 md:grid-cols-3 gap-6 lg:gap-8">
            {amenities.map((amenity, index) => (
              <motion.div
                key={amenity.title}
                className="bg-sand-light/30 rounded-2xl p-6 lg:p-8 text-center card-hover"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: index * 0.05 }}
                data-testid={`amenity-${amenity.title.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <div className="w-14 h-14 rounded-full bg-white flex items-center justify-center mx-auto mb-4 shadow-sm">
                  <amenity.icon className="w-7 h-7 text-sea" strokeWidth={1.5} />
                </div>
                <h3 className="font-serif text-lg font-semibold text-brand mb-2">
                  {amenity.title}
                </h3>
                <p className="font-sans text-sm text-muted-foreground">
                  {amenity.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-24 bg-brand" data-testid="testimonials-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            className="text-center max-w-2xl mx-auto mb-16"
            {...fadeUp}
          >
            <h2 className="font-serif text-3xl sm:text-4xl font-bold text-white mb-4">
              What Our Members Say
            </h2>
            <p className="font-sans text-white/70 leading-relaxed">
              Join a community of satisfied professionals who love working at Coastal Collaborative.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {testimonials.map((testimonial, index) => (
              <motion.div
                key={testimonial.name}
                className="bg-white/10 backdrop-blur rounded-2xl p-8 lg:p-10"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                data-testid={`testimonial-${testimonial.name.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <Quote className="w-10 h-10 text-sea mb-6" strokeWidth={1.5} />
                <p className="font-sans text-white/90 leading-relaxed mb-6">
                  "{testimonial.text}"
                </p>
                <div>
                  <p className="font-serif text-lg font-semibold text-white">
                    {testimonial.name}
                  </p>
                  <p className="font-sans text-sm text-sea">
                    {testimonial.role}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-sand-light/30" data-testid="cta-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="font-serif text-3xl sm:text-4xl font-bold text-brand mb-4">
              Ready to Elevate Your Workday in Brevard County?
            </h2>
            <p className="font-sans text-lg text-muted-foreground leading-relaxed mb-8 max-w-2xl mx-auto">
              Schedule a tour today and discover why Coastal Collaborative is the perfect coworking space 
              for freelancers, entrepreneurs, and growing businesses in Satellite Beach, Indialantic, and Brevard County.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact">
                <Button 
                  size="lg" 
                  className="bg-brand text-white hover:bg-brand-light rounded-full px-8 btn-primary"
                  data-testid="cta-tour-button"
                >
                  Request a Tour
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
              <a href="tel:321-866-8572">
                <Button 
                  size="lg" 
                  variant="outline" 
                  className="border-brand text-brand hover:bg-brand hover:text-white rounded-full px-8"
                  data-testid="cta-phone-button"
                >
                  <Phone className="mr-2 h-5 w-5" />
                  Call (321) 866-8572
                </Button>
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
